import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/lib/utils";

interface MonthlySummaryViewProps {
  summaries: Array<{
    month_year: string;
    year: number;
    allocated: number;
    requested: number;
    granted: number;
    disbursed: number;
    count: number;
  }>;
}

export function MonthlySummaryView({ summaries }: MonthlySummaryViewProps) {
  if (summaries.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Monthly Summary</CardTitle>
          <CardDescription className="text-sm">
            Monthly fund activity overview
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">No data available for selected years</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Monthly Summary</CardTitle>
        <CardDescription className="text-sm">
          Monthly fund activity versus yearly allocations - allocated amounts shown are for the entire year
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
          {summaries.map((summary) => (
            <Card key={summary.month_year} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  {summary.month_year}
                  <Badge variant="outline" className="text-xs">
                    {summary.count} requests
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Allocated (Year):</span>
                  <span className="font-medium">{formatCurrency(summary.allocated)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Requested:</span>
                  <span className="font-medium">{formatCurrency(summary.requested)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Granted:</span>
                  <span className="font-medium">{formatCurrency(summary.granted)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Disbursed:</span>
                  <span className="font-medium">{formatCurrency(summary.disbursed)}</span>
                </div>
                <Separator className="my-2" />
                <div className="pt-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">Grant / Requested:</span>
                    <Badge variant="secondary" className="text-xs">
                      {summary.allocated > 0 ? ((summary.granted / summary.requested) * 100).toFixed(0) : 0}%
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center text-xs mt-1">
                    <span className="text-muted-foreground">Disbursement / Granted:</span>
                    <Badge variant="secondary" className="text-xs">
                      {summary.granted > 0 ? ((summary.disbursed / summary.granted) * 100).toFixed(0) : 0}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
